import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { rpc, type Policy } from "./rpc";
import type {
  Doc,
  DocumentPreferences,
  FilterCondition,
  Group,
  Membership,
  NavigationNode,
  Permission,
  ResourceGroupMembership,
  Star,
  User,
} from "./types";

/**
 * The document read and write surface (`docs/spec/documents.md` §4). Every list
 * in the product — home tabs, drafts, archive, trash, a collection tab, the
 * backlinks panel — is the same call with a different body, so there is one hook
 * and one body builder, and the builders are exported because what a screen
 * *sends* is the thing worth asserting in a test.
 */

export type DocumentSort =
  "createdAt" | "updatedAt" | "publishedAt" | "title" | "popularityScore" | "index";

export type SortDirection = "ASC" | "DESC";

export interface DocumentListBody {
  collectionId?: string | undefined;
  userId?: string | undefined;
  parentDocumentId?: string | null | undefined;
  backlinkDocumentId?: string | undefined;
  template?: boolean | undefined;
  statusFilter?: ("draft" | "archived" | "published")[] | undefined;
  filters?: FilterCondition[] | undefined;
  sort?: DocumentSort | undefined;
  direction?: SortDirection | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export interface DocumentPage {
  documents: Doc[];
  /** Per-document abilities, indexed by document id, from `policies`. */
  abilities: Record<string, Record<string, boolean>>;
  total: number;
}

export function indexAbilities(policies: Policy[]): Record<string, Record<string, boolean>> {
  const index: Record<string, Record<string, boolean>> = {};
  for (const policy of policies) {
    index[policy.id] = policy.abilities ?? {};
  }
  return index;
}

/** Drops undefined keys so a recorded request body is exactly what was meant. */
export function compact(body: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(body)) {
    if (value !== undefined) result[key] = value;
  }
  return result;
}

export const documentQueryKeys = {
  list: (method: string, body: DocumentListBody) => ["documents", method, body] as const,
  info: (id: string) => ["documents.info", id] as const,
  structure: (id: string) => ["collections.documents", id] as const,
  stars: ["stars.list"] as const,
  shared: ["userMemberships.list"] as const,
};

async function fetchPage(method: string, body: DocumentListBody): Promise<DocumentPage> {
  const result = await rpc<Doc[]>(method, compact({ limit: 25, ...body }));
  return {
    documents: result.data,
    abilities: indexAbilities(result.policies),
    total: result.pagination?.total ?? result.data.length,
  };
}

/**
 * A page of documents from any of the list endpoints. `method` is part of the
 * key, so `/drafts` and `/archive` never share a cache entry.
 */
export function useDocumentPage(
  method: string,
  body: DocumentListBody = {},
  enabled = true,
): UseQueryResult<DocumentPage> {
  return useQuery({
    queryKey: documentQueryKeys.list(method, body),
    queryFn: () => fetchPage(method, body),
    enabled,
    retry: false,
    placeholderData: (previous: DocumentPage | undefined) => previous,
  });
}

export interface DocumentInfo {
  document: Doc;
  abilities: Record<string, boolean>;
}

/** `documents.info` accepts a UUID or a slug, so the route parameter goes straight in. */
export function useDocument(id: string | undefined): UseQueryResult<DocumentInfo> {
  return useQuery({
    queryKey: documentQueryKeys.info(id ?? ""),
    enabled: id !== undefined && id !== "",
    retry: false,
    queryFn: async () => {
      const result = await rpc<Doc>("documents.info", { id });
      return {
        document: result.data,
        abilities: result.policies.find((policy) => policy.id === result.data.id)?.abilities ?? {},
      };
    },
  });
}

/** The collection's navigation tree, fetched only once a node is expanded. */
export function useCollectionStructure(
  id: string | undefined,
  enabled = true,
): UseQueryResult<NavigationNode[]> {
  return useQuery({
    queryKey: documentQueryKeys.structure(id ?? ""),
    enabled: enabled && id !== undefined && id !== "",
    retry: false,
    queryFn: async () => (await rpc<NavigationNode[]>("collections.documents", { id })).data,
  });
}

/* --------------------------------------------------------------- mutations */

export interface DocumentUpdateInput {
  id: string;
  title?: string;
  text?: string;
  icon?: string | null;
  color?: string | null;
  fullWidth?: boolean;
  preferences?: DocumentPreferences | null;
  templateId?: string | null;
  collectionId?: string;
  publish?: boolean;
  lastRevision?: number;
  done?: boolean;
}

export interface DocumentCreateInput {
  title?: string;
  text?: string;
  collectionId?: string | null;
  parentDocumentId?: string | null;
  templateId?: string | null;
  template?: boolean;
  publish?: boolean;
  index?: number;
}

export interface DocumentMoveInput {
  id: string;
  collectionId?: string | null;
  parentDocumentId?: string | null;
  index?: number;
}

/** Everything that changes a document invalidates every list and its own record. */
export function invalidateDocuments(client: QueryClient, id?: string): void {
  void client.invalidateQueries({ queryKey: ["documents"] });
  void client.invalidateQueries({ queryKey: ["collections.documents"] });
  if (id !== undefined) {
    void client.invalidateQueries({ queryKey: documentQueryKeys.info(id) });
  }
}

function makeDocumentMutation<TInput extends { id?: string }>(
  method: string,
): () => UseMutationResult<Doc, Error, TInput> {
  return function useMethod(): UseMutationResult<Doc, Error, TInput> {
    const client = useQueryClient();
    return useMutation({
      mutationFn: async (input: TInput) =>
        (await rpc<Doc>(method, compact(input as Record<string, unknown>))).data,
      onSuccess: (_data, input) => {
        invalidateDocuments(client, input.id);
      },
    });
  };
}

export const useCreateDocument = makeDocumentMutation<DocumentCreateInput & { id?: string }>(
  "documents.create",
);
export const useUpdateDocument = makeDocumentMutation<DocumentUpdateInput>("documents.update");
export const useArchiveDocument = makeDocumentMutation<{ id: string }>("documents.archive");
export const useUnarchiveDocument = makeDocumentMutation<{ id: string }>("documents.restore");
export const useRestoreDocument = makeDocumentMutation<{ id: string; revisionId?: string }>(
  "documents.restore",
);
export const useDeleteDocument = makeDocumentMutation<{ id: string; permanent?: boolean }>(
  "documents.delete",
);
export const useUnpublishDocument = makeDocumentMutation<{ id: string; detach?: boolean }>(
  "documents.unpublish",
);
export const useDuplicateDocument = makeDocumentMutation<{
  id: string;
  title?: string;
  recursive?: boolean;
  publish?: boolean;
  collectionId?: string | null;
  parentDocumentId?: string | null;
}>("documents.duplicate");
export const useTemplatizeDocument = makeDocumentMutation<{
  id: string;
  collectionId?: string | null;
  publish: boolean;
}>("documents.templatize");
/**
 * `documents.move` answers with every record the move touched rather than one
 * document, because moving a subtree changes its descendants' collection too.
 */
export interface MoveResult {
  documents: Doc[];
  collections: unknown[];
}

export function useMoveDocument(): UseMutationResult<MoveResult, Error, DocumentMoveInput> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: DocumentMoveInput) =>
      (await rpc<MoveResult>("documents.move", compact({ ...input }))).data,
    onSettled: (_data, _error, input) => {
      invalidateDocuments(client, input.id);
    },
  });
}

export function useEmptyTrash(): UseMutationResult<unknown, Error, void> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => (await rpc("documents.empty_trash", {})).data,
    onSuccess: () => {
      invalidateDocuments(client);
    },
  });
}

/* ------------------------------------------------------------------ stars */

export interface StarredEntry {
  star: Star;
  document?: Doc;
}

export function useStars(enabled = true): UseQueryResult<{ stars: Star[]; documents: Doc[] }> {
  return useQuery({
    queryKey: documentQueryKeys.stars,
    enabled,
    retry: false,
    queryFn: async () => {
      const result = await rpc<Star[] | { stars?: Star[]; documents?: Doc[] }>("stars.list", {
        limit: 100,
      });
      // The server answers with a plain array of stars; a shape that also
      // carries the starred documents is tolerated so a future widening of the
      // endpoint does not break the sidebar.
      if (Array.isArray(result.data)) return { stars: result.data, documents: [] };
      return { stars: result.data.stars ?? [], documents: result.data.documents ?? [] };
    },
  });
}

function makeStarMutation<TInput>(method: string): () => UseMutationResult<Star, Error, TInput> {
  return function useMethod(): UseMutationResult<Star, Error, TInput> {
    const client = useQueryClient();
    return useMutation({
      mutationFn: async (input: TInput) =>
        (await rpc<Star>(method, compact(input as Record<string, unknown>))).data,
      onSuccess: () => {
        void client.invalidateQueries({ queryKey: documentQueryKeys.stars });
      },
    });
  };
}

export const useCreateStar = makeStarMutation<{
  documentId?: string;
  collectionId?: string;
  index?: string;
}>("stars.create");
export const useDeleteStar = makeStarMutation<{ id: string }>("stars.delete");
export const useUpdateStar = makeStarMutation<{ id: string; index: string }>("stars.update");

/* ------------------------------------------------------- shared with me */

export interface SharedWithMe {
  memberships: Membership[];
  documents: Doc[];
}

export function useSharedWithMe(enabled = true): UseQueryResult<SharedWithMe> {
  return useQuery({
    queryKey: documentQueryKeys.shared,
    enabled,
    retry: false,
    queryFn: async () => {
      const result = await rpc<SharedWithMe>("userMemberships.list", { limit: 100 });
      return {
        memberships: result.data.memberships ?? [],
        documents: result.data.documents ?? [],
      };
    },
  });
}

/** `views.create` records that the caller opened a document (note 03 §4). */
export function useRecordView(): UseMutationResult<unknown, Error, { documentId: string }> {
  return useMutation({
    mutationFn: async (input: { documentId: string }) => (await rpc("views.create", input)).data,
  });
}

/* ------------------------------------------------- document memberships */

/**
 * Who reaches a document directly (`docs/spec/documents.md` memberships,
 * `docs/spec/permissions.md` §4). `documents.users` is the wider answer — it
 * includes people who reach it through the collection — and is what the share
 * dialog lists as read-only rows beside the grants it can actually change.
 */
export interface DocumentMembers {
  memberships: Membership[];
  users: User[];
}

export interface DocumentGroupMembers {
  groupMemberships: ResourceGroupMembership[];
  groups: Group[];
}

export const membershipQueryKeys = {
  users: (id: string) => ["documents.users", id] as const,
  memberships: (id: string) => ["documents.memberships", id] as const,
  groupMemberships: (id: string) => ["documents.group_memberships", id] as const,
};

export function useDocumentMembers(
  id: string | undefined,
  enabled = true,
): UseQueryResult<DocumentMembers> {
  return useQuery({
    queryKey: membershipQueryKeys.memberships(id ?? ""),
    enabled: enabled && id !== undefined && id !== "",
    retry: false,
    queryFn: async () => {
      const result = await rpc<DocumentMembers>("documents.memberships", { id, limit: 100 });
      return { memberships: result.data.memberships ?? [], users: result.data.users ?? [] };
    },
  });
}

export function useDocumentGroupMembers(
  id: string | undefined,
  enabled = true,
): UseQueryResult<DocumentGroupMembers> {
  return useQuery({
    queryKey: membershipQueryKeys.groupMemberships(id ?? ""),
    enabled: enabled && id !== undefined && id !== "",
    retry: false,
    queryFn: async () => {
      const result = await rpc<DocumentGroupMembers>("documents.group_memberships", {
        id,
        limit: 100,
      });
      return {
        groupMemberships: result.data.groupMemberships ?? [],
        groups: result.data.groups ?? [],
      };
    },
  });
}

/** Everyone who can read the document, however they got there. */
export function useDocumentUsers(id: string | undefined, enabled = true): UseQueryResult<User[]> {
  return useQuery({
    queryKey: membershipQueryKeys.users(id ?? ""),
    enabled: enabled && id !== undefined && id !== "",
    retry: false,
    queryFn: async () => (await rpc<User[]>("documents.users", { id, limit: 100 })).data,
  });
}

function makeAccessMutation<TInput extends { id: string }>(
  method: string,
): () => UseMutationResult<unknown, Error, TInput> {
  return function useMethod(): UseMutationResult<unknown, Error, TInput> {
    const client = useQueryClient();
    return useMutation({
      mutationFn: async (input: TInput) => (await rpc(method, input)).data,
      onSuccess: (_data, input) => {
        void client.invalidateQueries({ queryKey: membershipQueryKeys.memberships(input.id) });
        void client.invalidateQueries({
          queryKey: membershipQueryKeys.groupMemberships(input.id),
        });
        void client.invalidateQueries({ queryKey: membershipQueryKeys.users(input.id) });
      },
    });
  };
}

export const useDocumentAddUser = makeAccessMutation<{
  id: string;
  userId: string;
  permission: Permission;
}>("documents.add_user");
export const useDocumentRemoveUser = makeAccessMutation<{ id: string; userId: string }>(
  "documents.remove_user",
);
export const useDocumentAddGroup = makeAccessMutation<{
  id: string;
  groupId: string;
  permission: Permission;
}>("documents.add_group");
export const useDocumentRemoveGroup = makeAccessMutation<{ id: string; groupId: string }>(
  "documents.remove_group",
);
